import type { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import type {
  DeliveryReconCellRendererParams,
  DeliveryReconDetailRow,
  DeliveryReconGridActions,
  DeliveryReconOverviewRow,
} from './types'

import { formatters } from '@/utils/intl/dateTimeFormatters'
import { Tooltip } from 'antd'
import { ActionsCellRenderer } from './components/ActionsCellRenderer'
import { addCommasToNumber, BBDTag, GraviButton, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import { StatusTag } from './components/StatusTag'
import { OrderUpdatedTag } from '@/components/OrderDispatch/OrderGrid/OrderUpdatedTag'
import { NumberListCellRenderer } from './components/NumberListCellRenderer'
import { getTimeVariance, formatDuration, getOrderNumberForPo } from './util'
import { startCase, uniqBy } from 'lodash'
import { getComparator } from '@/utils/grid'

const getColumnDefinitions = (actions: DeliveryReconGridActions): Record<string, ColDef> => ({
  checkbox: {
    checkboxSelection: (params) => params.data.status !== 'voided' && params.data.status !== 'approved',
    cellRenderer: 'agGroupCellRenderer',
    maxWidth: 80,
    sortable: false,
    suppressMenu: true,
  },
  store: {
    field: 'store_number',
    headerName: 'Site',
    flex: 1.5,
    comparator: (a, b) => getComparator(a, b, 'site'),
    valueGetter: (params) => `${params?.data?.store_number} - ${params.data?.store_name}`,
  },
  source: {
    field: 'source',
    headerName: 'Source',
    comparator: (a, b) => getComparator(a, b, 'string'),
    valueGetter: (params: ValueGetterParams<DeliveryReconDetailRow>) => params.data?.source ?? '',
    cellRenderer: (params: ICellRendererParams<DeliveryReconDetailRow>) => {
      const { source, type } = params.data ?? {}
      const isOrder = type === 'bol'
      return (
        <Horizontal>
          <MaterialIcon
            className='pr-2'
            icon={isOrder ? 'description' : 'opacity'}
            size={16}
            style={{ paddingTop: '3px' }}
          />
          <Texto>{source}</Texto>
        </Horizontal>
      )
    },
  },
  products: {
    field: 'products',
    headerName: 'Products',
    flex: 2,
    autoHeight: true,
    cellRenderer: (params: DeliveryReconCellRendererParams) => (
      <Horizontal gap='0.5em' style={{ flexWrap: 'wrap' }}>
        {params.value?.map((p: DeliveryReconOverviewRow['products'][number]) => (
          <BBDTag className='text-ellipsis' key={p}>
            {p}
          </BBDTag>
        ))}
      </Horizontal>
    ),
  },
  product: {
    field: 'source_product',
    headerName: 'Product',
    rowGroup: true,
    sort: 'asc',
  },
  date: {
    field: 'date',
    headerName: 'Date',
    comparator: (a, b) => getComparator(a, b, 'date'),
    valueGetter: (params) => {
      const date = params.data?.match_date ?? params.data?.date
      const tz = params.data?.match_date_local ?? params.data?.date_local
      return date ? formatters.DATE(date, tz) : ''
    },
  },
  time: {
    field: 'date',
    headerName: 'Time',
    comparator: (a, b) => getComparator(a, b, 'time'),
    valueGetter: (params) => {
      const date = params.data?.match_date ?? params.data?.date
      const tz = params.data?.match_date_local ?? params.data?.date_local
      return date ? formatters.TIME(date, tz) : ''
    },
  },
  volume: {
    field: 'volume',
    headerName: 'Volume (qty)',
    comparator: (a, b) => getComparator(a, b, 'number'),
    type: 'rightAligned',
    valueFormatter: (params: ValueFormatterParams) => addCommasToNumber(params.value) ?? '0',
  },
  number: {
    headerName: 'number',
    field: 'source_identifier',
    valueGetter: (params: ValueGetterParams<DeliveryReconDetailRow>) =>
      params.data?.source_identifier?.map((d) => d.number) ?? [],
    cellRenderer: (params: ICellRendererParams<DeliveryReconDetailRow>) => (
      <NumberListCellRenderer values={params.value} />
    ),
  },
  status: {
    field: 'status',
    headerName: 'Status',
    flex: 1.75,
    filter: 'agSetColumnFilter',
    valueGetter: (params: ValueGetterParams<DeliveryReconOverviewRow>) => {
      const status = params.data?.status
      const type = params.data?.details?.every((d) => d.type === 'bol') ? 'bol' : 'delivery'
      if (status === 'unmatched') return type === 'bol' ? 'Ord. Unmatched' : 'Meas. Unmatched'
      return startCase(status)
    },
    cellRenderer: (params: DeliveryReconCellRendererParams) => {
      return (
        <StatusTag
          status={params.value}
          type={params.data?.details?.every((d) => d.type === 'bol') ? 'bol' : 'delivery'}
        />
      )
    },
  },
  actions: {
    field: '',
    headerName: 'Actions',
    sortable: false,
    pinned: 'right',
    maxWidth: 105,
    cellRenderer: (params: DeliveryReconCellRendererParams) => (
      <ActionsCellRenderer params={params} action={actions?.onApprove} />
    ),
  },
  detail_actions: {
    field: '',
    maxWidth: 120,
    headerName: 'Actions',
    sortable: false,
    cellRenderer: (params: DeliveryReconCellRendererParams) => {
      const parentData = params.context.parentData
      const hasOneItem =
        uniqBy(parentData?.details, (item: DeliveryReconDetailRow) => item.source_identifier?.[0].number).length <= 1
      const isApproved = parentData?.status === 'approved'
      const disabled = hasOneItem || isApproved
      return (
        <Horizontal justifyContent='center' width='100%' height='100%' verticalCenter>
          <Tooltip
            title={isApproved ? 'Cannot unmatch when approved' : hasOneItem ? "Can't unmatch only item" : 'Unmatch'}
          >
            <GraviButton
              disabled={disabled}
              icon={<MaterialIcon className='pt-1' icon='link_off' />}
              type='text'
              onClick={() => actions.onUnlink?.(params.data)}
              style={{ height: 25 }}
            />
          </Tooltip>
        </Horizontal>
      )
    },
  },
  updated: {
    headerName: 'Updated',
    field: 'updated_on',
    flex: 1.5,
    valueFormatter: (params: ValueFormatterParams) => formatters.DATE_TIME(params.value) ?? '',
    cellRenderer: (params: ICellRendererParams) => (
      <OrderUpdatedTag
        viewHistoryLabel='View Match History'
        setAuditOrderId={() => actions.onViewHistory?.(params.data)}
        order={{
          updated_on: params.value,
          updated_by: params.data?.updated_by,
          created_date: params.data?.match_date,
        }}
      />
    ),
  },
  bol_volume: {
    headerName: 'BOL Vol.',
    field: 'bol_volume',
    type: 'rightAligned',
    valueFormatter: (params: ValueFormatterParams) => addCommasToNumber(params.value) ?? '0',
  },
  delivery_volume: {
    headerName: 'Measured Vol.',
    field: 'delivery_volume',
    type: 'rightAligned',
    valueFormatter: (params: ValueFormatterParams) => addCommasToNumber(params.value) ?? '0',
  },
  variance: {
    headerName: 'Vol. Variance',
    field: 'variance',
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: (params: ValueFormatterParams) => addCommasToNumber(params.value) ?? '0',
    valueGetter: (params) => Math.abs(params.data.bol_volume - params.data.delivery_volume),
  },
  time_variance: {
    headerName: 'Time Variance',
    field: 'time_variance',
    comparator: (a, b) => a - b,
    valueGetter: (params: ValueGetterParams<DeliveryReconOverviewRow>) => getTimeVariance(params.data?.details),
    valueFormatter: (params) => formatDuration(params.value),
  },
  orders: {
    field: 'orders',
    headerName: 'Order #',
    autoHeight: true,
    valueGetter: (params) => params.data?.orders?.map((o: DeliveryReconOverviewRow['orders'][number]) => o.number),
    cellRenderer: (params: DeliveryReconCellRendererParams) => (
      <NumberListCellRenderer values={params.value} link='/order_movements_page/report' />
    ),
  },
  po_numbers: {
    field: 'po_numbers',
    headerName: 'PO #',
    autoHeight: true,
    cellRenderer: (params: DeliveryReconCellRendererParams) => (
      <NumberListCellRenderer
        values={params.value}
        link='/order_movements_page/report'
        getOrderNumber={(po: DeliveryReconOverviewRow['po_numbers'][number]) =>
          getOrderNumberForPo(params.data?.details, po)
        }
      />
    ),
  },
  bol_numbers: {
    field: 'bol_numbers',
    headerName: 'BOL #',
    autoHeight: true,
    valueGetter: (params) => params.data?.bol_numbers,
    cellRenderer: (params: DeliveryReconCellRendererParams) => <NumberListCellRenderer values={params.value} />,
  },
  measured_number: {
    field: 'delivery_numbers',
    headerName: 'Measured #',
    autoHeight: true,
    valueGetter: (params) => params.data?.delivery_numbers,
    cellRenderer: (params: DeliveryReconCellRendererParams) => <NumberListCellRenderer values={params.value} />,
  },
})

const getColumns = (keys: string[], actions: DeliveryReconGridActions) => {
  const COLUMNS = getColumnDefinitions(actions)
  return keys.map((key) => COLUMNS[key]).filter(Boolean)
}

export const getReportColumnDefs = (actions: DeliveryReconGridActions): ColDef[] =>
  getColumns(
    [
      'checkbox',
      'store',
      'status',
      'orders',
      'po_numbers',
      'bol_numbers',
      'measured_number',
      'products',
      'date',
      'time',
      'updated',
      'bol_volume',
      'delivery_volume',
      'variance',
      'time_variance',
      'actions',
    ],
    actions
  )

export const getDetailColumnDefs = (onUnlink: (item: DeliveryReconDetailRow) => Promise<void>): ColDef[] =>
  getColumns(['number', 'source', 'product', 'site', 'date', 'time', 'volume', 'detail_actions'], { onUnlink })
